import React, { lazy, Suspense, useMemo, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Box, Grid, Preloader } from '@common-fe/common-fe';
import styled from 'styled-components';

import { forceRefreshToken } from '@/api/api';
import ROUTES from '@/common/routes';
import { FullLoader } from '@/components/elements/FullLoader/FullLoader';
import { OrgLockBanner } from '@/modules/core/components/OrgLockBanner';
import Sidebar, { SidebarShimmerBox } from '@/modules/core/components/Sidebar';
import Zendesk from '@/modules/core/components/Zendesk/Zendesk';
import {
  useCurrentOrganizationPathChecker,
  useLogSSOError,
  useOrganizationFavicon,
  useSsoLogout,
} from '@/modules/core/hooks';
import useTopBannersOffset from '@/modules/core/hooks/useTopBannersOffset';
import useWhiteLabelingFavicon from '@/modules/core/hooks/useWhiteLabelingFavicon';
import { EmployerTerminationBanner } from '@/modules/employer/components/EmployerTermination/EmployerTerminationBanner';
import UnappliedFundsBanner, {
  UNAPPLIED_FUNDS_DASHBOARD_PERMISSIONS,
  UnappliedFundsBannerVariant,
} from '@/modules/UnappliedFundsBanner';
import { useAuthStore } from '@/modules/user/stores';
import { useVersionChecker } from '@/utils';
import { matchPath } from '@/utils/modifiers';

import { ErrorBoundary } from '../ErrorBoundary';
import Notifications from '../Notifications';
import Snackbar from '../Snackbar';

import useAppRedirect from './useAppRedirect';

import './App.css';

const TimeoutModal = lazy(() => import('../TimeoutModal'));

const Content = styled(Box)`
  flex: 1;
  padding-left: 0px;
  overflow-x: auto;
`;

const WITHOUT_WRAPPER_PAGES = [
  ROUTES.EXPRESS_SETUPS,
  ROUTES.EXPRESS_SETUPS_CREATE_ORGANIZATION,
  ROUTES.CONTACT_VERIFICATION,
  ROUTES.RESET_PASSWORD,
];

forceRefreshToken();
function App() {
  const { routes, minifiedQuery, errorMode } = useAppRedirect();
  const { inited, auth, user } = useAuthStore();
  const { pathname } = useLocation();
  const { topBannersHeight, contentOffset } = useTopBannersOffset();
  const isHomePage = pathname === ROUTES.HOME;
  const isExpressSetupPage = useMemo(() => pathname.includes(ROUTES.EXPRESS_SETUPS), [pathname]);
  const isRenewalSetupPage = useMemo(() => pathname.includes(ROUTES.RENEWALS_SETUP), [pathname]);
  useOrganizationFavicon();
  useCurrentOrganizationPathChecker();
  useVersionChecker();
  useWhiteLabelingFavicon();
  useSsoLogout({ isLoggedIn: auth });
  useLogSSOError();

  const [minified] = useState(minifiedQuery);

  const isWithWrapper = useMemo(
    () => !WITHOUT_WRAPPER_PAGES.some((template) => matchPath(template, pathname)),
    [pathname]
  );

  if (!inited) {
    return null;
  }

  if (auth && isWithWrapper) {
    return (
      <>
        <FullLoader />

        <Suspense fallback={null}>
          <TimeoutModal key={`${auth}`} />
        </Suspense>
        <OrgLockBanner />
        <EmployerTerminationBanner />
        {isHomePage && !!user && (
          <UnappliedFundsBanner
            variant={UnappliedFundsBannerVariant.HOME}
            permissions={UNAPPLIED_FUNDS_DASHBOARD_PERMISSIONS}
            top={topBannersHeight}
          />
        )}
        <Grid
          id="modal-portal-id"
          fill
          rows={['flex']}
          columns={isExpressSetupPage || isRenewalSetupPage || minified ? ['flex'] : ['auto', 'flex']}
          areas={[
            { name: 'sidebar', start: [0, 0], end: [0, 0] },
            { name: 'content', start: [1, 0], end: [1, 0] },
          ]}
          style={{ height: `calc(100vh - ${contentOffset})` }}
          margin={{ top: contentOffset }}
        >
          <ErrorBoundary>
            <Snackbar />
          </ErrorBoundary>
          <ErrorBoundary>
            {!minified &&
              !errorMode &&
              !isExpressSetupPage &&
              !isRenewalSetupPage &&
              (user ? <Sidebar /> : <SidebarShimmerBox />)}
          </ErrorBoundary>

          <Notifications />
          <Content id="content_wrapper" direction="row">
            {/* <Box direction="row"> */}
            <ErrorBoundary>
              <Suspense fallback={<Preloader />}>
                <Switch>
                  {routes.map((route) => (
                    <Route key={`${route.path}`} {...route} />
                  ))}
                </Switch>
              </Suspense>
            </ErrorBoundary>
            {/* </Box> */}
          </Content>
        </Grid>
        <Zendesk />
      </>
    );
  }
  return (
    <ErrorBoundary>
      <Snackbar />
      <Suspense fallback={<Preloader />}>
        <Switch>
          {routes.map((route) => (
            <Route exact key={`${route.path}`} path={route.path}>
              {route.component as unknown as string}
            </Route>
          ))}
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
